// Widget registry — defines all available dashboard widgets.
// `requiredModule`: must appear in localStorage "authorizedModules" (null = always shown).
// `defaultEnabled`: shown by default for new users.
// `cols`: CSS grid column span (out of 12).

// rows: 1 = short widget, rows: 3 = long widget (3 shorts = 1 long including gaps)
// cols: 4 = 1/3 width, cols: 8 = 2/3 width, cols: 12 = full width
export const WIDGET_REGISTRY = [
  // ── Short widgets (rows: 1) ───────────────────────────────────────────────
  {
    id: "revenue",
    title: "Revenue Collected",
    description: "Total payments received this month",
    requiredModule: "finance",
    defaultEnabled: true,
    cols: 4,
    rows: 1,
    multiInstance: true,
  },
  {
    id: "outstanding",
    title: "Outstanding Balances",
    description: "Unpaid invoices and overdue accounts",
    requiredModule: "finance",
    defaultEnabled: true,
    cols: 4,
    rows: 1,
    multiInstance: true,
  },
  {
    id: "memberCount",
    title: "Active Members",
    description: "Member counts and recent changes",
    requiredModule: "members",
    defaultEnabled: true,
    cols: 4,
    rows: 1,
  },
  {
    id: "donationBox",
    title: "Donation Box",
    description: "Donation box activity this month",
    requiredModule: "finance",
    defaultEnabled: true,
    cols: 4,
    rows: 1,
  },
  {
    id: "donations",
    title: "All Donations",
    description: "Total donations for the selected date range",
    requiredModule: "finance",
    defaultEnabled: true,
    cols: 4,
    rows: 1,
  },
  // ── Long widgets (rows: 3 = exactly 3 shorts including gaps) ─────────────
  {
    id: "yahrzeits",
    title: "Upcoming Yahrzeits",
    description: "Yahrzeits in the next 7 days",
    requiredModule: null,
    defaultEnabled: true,
    cols: 4,
    rows: 3,
  },
  {
    id: "birthdays",
    title: "Upcoming Birthdays",
    description: "Member birthdays in the next 30 days",
    requiredModule: "members",
    defaultEnabled: true,
    cols: 4,
    rows: 3,
  },
  {
    id: "anniversaries",
    title: "Upcoming Anniversaries",
    description: "Member anniversaries in the next 30 days",
    requiredModule: "members",
    defaultEnabled: true,
    cols: 4,
    rows: 3,
  },
  {
    id: "events",
    title: "Upcoming Events",
    description: "Next events with registration counts",
    requiredModule: "events",
    defaultEnabled: true,
    cols: 4,
    rows: 3,
  },
  {
    id: "recentTransactions",
    title: "Recent Transactions",
    description: "Latest payments across the org",
    requiredModule: "finance",
    defaultEnabled: true,
    cols: 4,
    rows: 3,
    multiInstance: true,
  },
  {
    id: "schoolEnrollment",
    title: "School Enrollment",
    description: "Enrollment vs. capacity per class",
    requiredModule: "school",
    defaultEnabled: true,
    cols: 4,
    rows: 3,
  },
  {
    id: "futurePayments",
    title: "Future Payments",
    description: "Projected monthly totals from active payment plans (next 6 months)",
    requiredModule: "finance",
    defaultEnabled: false,
    cols: 4,
    rows: 1,
  },
];

// Per-widget configurable settings definitions
export const WIDGET_SETTINGS = {
  yahrzeits: {
    dateType: {
      label: "Date calendar",
      options: [
        { value: "english", label: "English calendar" },
        { value: "hebrew",  label: "Hebrew calendar" },
      ],
      default: "english",
    },
  },
};

// Multi-instance widget helpers
export const getWidgetBaseId = (id) => id.split("__")[0];
export const createInstanceId = (baseId) => `${baseId}__${Date.now()}`;
export const getRegistryEntry = (id) => WIDGET_REGISTRY.find((w) => w.id === getWidgetBaseId(id));

const STORAGE_KEY_PREFIX = "dashboard_widgets_";
const SETTINGS_KEY_PREFIX = "dashboard_widget_settings_";

export const getStorageKey = () => {
  try {
    const org = JSON.parse(localStorage.getItem("organization"));
    return `${STORAGE_KEY_PREFIX}${org?._id || "default"}`;
  } catch {
    return `${STORAGE_KEY_PREFIX}default`;
  }
};

export const getAuthorizedModules = () => {
  try {
    return JSON.parse(localStorage.getItem("authorizedModules")) || [];
  } catch {
    return [];
  }
};

export const getOrgAuthorizedModules = () => {
  try {
    return JSON.parse(localStorage.getItem("orgAuthorizedModules")) || [];
  } catch {
    return [];
  }
};

export const isModuleActive = (moduleName) => {
  const userModules = getAuthorizedModules();
  // super admin: no modules stored → allow everything
  if (!userModules.length) return true;
  const userMod = userModules.find((m) => m?.moduleName?.toLowerCase() === moduleName.toLowerCase());
  // not in user list = not restricted
  if (!userMod) return true;
  return userMod.active !== false;
};

export const isWidgetPermitted = (widget) => {
  if (!widget.requiredModule) return true;
  return isModuleActive(widget.requiredModule);
};

export const loadEnabledWidgets = () => {
  try {
    const key = getStorageKey();
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch {}
  // First visit: return default-enabled widgets the user has permission for
  return WIDGET_REGISTRY.filter(
    (w) => w.defaultEnabled && isWidgetPermitted(w)
  ).map((w) => w.id);
};

export const saveEnabledWidgets = (widgetIds) => {
  try {
    localStorage.setItem(getStorageKey(), JSON.stringify(widgetIds));
  } catch {}
};

const getSettingsKey = () => {
  try {
    const org = JSON.parse(localStorage.getItem("organization"));
    return `${SETTINGS_KEY_PREFIX}${org?._id || "default"}`;
  } catch {
    return `${SETTINGS_KEY_PREFIX}default`;
  }
};

export const loadWidgetSettings = () => {
  try {
    const stored = localStorage.getItem(getSettingsKey());
    if (stored) return JSON.parse(stored);
  } catch {}
  // Build defaults from WIDGET_SETTINGS
  const defaults = {};
  Object.entries(WIDGET_SETTINGS).forEach(([widgetId, fields]) => {
    defaults[widgetId] = {};
    Object.entries(fields).forEach(([key, def]) => {
      defaults[widgetId][key] = def.default;
    });
  });
  return defaults;
};

export const saveWidgetSettings = (settings) => {
  try {
    localStorage.setItem(getSettingsKey(), JSON.stringify(settings));
  } catch {}
};
